<script setup lang="ts">
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useRouter, type RouteLocationRaw} from 'vue-router';
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {isSameRoutePath} from '@/ts/utils/pure-functions';

const props = withDefaults(defineProps<{
  to?: RouteLocationRaw;
  label?: string;
  hideArrow?: boolean;
}>(), {
  hideArrow: false,
});

const router = useRouter();
const {t: $t} = useI18n();

const backLabel = computed(() => {
  if (props.label) {
    return props.label;
  }
  const state = window.history.state as {backLabel?: string};
  const stateLabel = state?.backLabel;
  return stateLabel ? $t(stateLabel) : $t('back');
});

function previousHistoryPath(): string {
  const state = window.history.state as {back?: string | null} | null;
  return typeof state?.back === 'string' ? state.back : '';
}

function goBack(): void {
  if (props.to) {
    const previous = previousHistoryPath();
    // A real back restores the previous entry's scroll position and page state; push only
    // when that entry isn't the destination (deep link, or arrived from somewhere else).
    if (previous && isSameRoutePath(previous, router.resolve(props.to).fullPath)) {
      router.back();
      return;
    }
    void router.push(props.to);
    return;
  }
  router.back();
}

</script>
<template>
  <div class="back-link">
    <button type="button" class="link" @click="goBack">
      <svg-illustration class="back-arrow" name="chevron-left.svg"/>
      <span class="label">{{ backLabel }}</span>
    </button>
    <svg-illustration v-if="!props.hideArrow" name="right-down-arrow.svg" inner-class="inner-right"/>
  </div>
</template>
<style lang="scss" scoped>
.back-link {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: var(--Spacing-spacing-2);

  @include screen-lg {
    margin-bottom: var(--Spacing-spacing-4);
  }
}

.link {
  display: flex;
  color: var(--Neutral-UI-neutral-ui-50);
  align-items: flex-start;
  margin-right: var(--Spacing-spacing-2);
  border-radius: 4px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  @include focus-visible;

  &:hover .label {
    text-decoration: underline;
  }
}

.label {
  @include font-h3-bold;
  color: var(--Neutral-UI-neutral-ui-100);
  text-align: left;
}

.back-arrow {
  position: relative;
  left: -6px;
  flex-shrink: 0;
}

</style>
